'use client'

import {
  useArtifactCanvas,
  useInviteToApply,
  getHrefWithThreadPreservation,
  buildApplicantRankByApplicationId,
} from '@yeshuman/sdk'
import { useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import {
  useApplicantDecision,
  useChangeApplicantStage,
  useOpportunityApplicants,
  type ApplicantCard,
} from './use-opportunity-applicants'
import { useApplicationNudge } from './use-application-nudge'
import { useJobAnalysisCoverage } from './use-job-analysis-coverage'
import type { JobProspectMatch } from './use-job-prospect-matches'
import { CandidatePanel } from '../components/employer/candidate-panel/candidate-panel'
import type { CandidatePanelSource } from '../components/employer/candidate-panel/candidate-panel-types'
import { useNavigate } from 'react-router-dom'

export function useJobWorkspaceCandidatePanel(jobId: string) {
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const navigate = useNavigate()
  const { board } = useOpportunityApplicants(jobId)
  const stages = board?.stages ?? []
  const changeStageMutation = useChangeApplicantStage(jobId)
  const decisionMutation = useApplicantDecision(jobId)
  const nudgeMutation = useApplicationNudge(jobId)
  const { inviteProfile, isApplied, isInvited, isInviting, invitingProfileId } = useInviteToApply(jobId)
  const { data: coverage } = useJobAnalysisCoverage(jobId)

  const rankByApplicationId = useMemo(
    () => buildApplicantRankByApplicationId(board?.applicants ?? []),
    [board?.applicants],
  )

  const renderPanel = useCallback(
    (source: CandidatePanelSource, origin: 'talent' | 'analysis' | 'suggestions' | 'applicants' = 'applicants') => {
      const applicant = source.kind === 'applicant' ? source.applicant : null
      const profileId = source.kind === 'applicant' ? source.applicant.profile.id : source.match.profile_id
      const trackingHref = applicant
        ? getHrefWithThreadPreservation(`/employer/jobs/${jobId}/applicants/${applicant.id}`)
        : null

      return (
        <CandidatePanel
          source={source}
          stages={stages}
          jobId={jobId}
          coverage={coverage ?? null}
          origin={origin}
          isUpdatingStage={changeStageMutation.isPending}
          isRecordingDecision={decisionMutation.isPending}
          isInviting={isInviting && invitingProfileId === profileId}
          nudgePending={nudgeMutation.isPending}
          applied={isApplied(profileId)}
          invited={isInvited(profileId)}
          onChangeStage={
            applicant
              ? (slug) =>
                  changeStageMutation.mutate(
                    { applicationId: applicant.id, stageSlug: slug },
                    { onError: (error) => toast.error(error.message) },
                  )
              : undefined
          }
          onRecordDecision={
            applicant
              ? (decision, reason) =>
                  decisionMutation.mutate(
                    { applicationId: applicant.id, decision, reason },
                    { onError: (error) => toast.error(error.message) },
                  )
              : undefined
          }
          onInvite={inviteProfile}
          onNudge={(applicationId) => nudgeMutation.mutate(applicationId)}
          onScheduleInterview={
            trackingHref ? () => navigate(trackingHref) : undefined
          }
        />
      )
    },
    [
      changeStageMutation,
      coverage,
      decisionMutation,
      inviteProfile,
      isApplied,
      isInvited,
      isInviting,
      invitingProfileId,
      jobId,
      navigate,
      nudgeMutation,
      stages,
    ],
  )

  const openApplicant = useCallback(
    (applicant: ApplicantCard, origin: 'talent' | 'analysis' | 'suggestions' | 'applicants' = 'applicants') => {
      const source: CandidatePanelSource = {
        kind: 'applicant',
        applicant,
        rank: rankByApplicationId.get(applicant.id),
      }
      openCanvas({
        preventAutoFocus: true,
        title: applicant.profile.name,
        content: renderPanel(source, origin),
      })
    },
    [openCanvas, rankByApplicationId, renderPanel],
  )

  const openProspect = useCallback(
    (match: JobProspectMatch, origin: 'talent' | 'analysis' | 'suggestions' = 'suggestions') => {
      const source: CandidatePanelSource = { kind: 'prospect', match }
      openCanvas({
        preventAutoFocus: true,
        title: match.name,
        content: renderPanel(source, origin),
      })
    },
    [openCanvas, renderPanel],
  )

  const openByProfileId = useCallback(
    (
      profileId: string,
      label: string,
      options?: { evaluationId?: string | null; origin?: 'talent' | 'analysis' | 'suggestions' | 'applicants' },
    ) => {
      const origin = options?.origin ?? 'talent'
      const applicant = (board?.applicants ?? []).find((entry) => entry.profile.id === profileId)
      if (applicant) {
        openApplicant(applicant, origin)
        return
      }
      const match: JobProspectMatch = {
        rank: 0,
        profile_id: profileId,
        name: label,
        headline: '',
        top_skills: [],
        semantic_score: 0,
        match_percent: 0,
        evaluation_id: options?.evaluationId ?? null,
      }
      openProspect(match, origin === 'talent' || origin === 'analysis' ? origin : 'suggestions')
    },
    [board?.applicants, openApplicant, openProspect],
  )

  return {
    openApplicant,
    openProspect,
    openByProfileId,
    closePanel: closeCanvas,
    renderPanel,
    rankByApplicationId,
  }
}
